import React from 'react';
import { Trans } from 'react-i18next';
import { useAppSelector } from 'flow/hooks/useRedux';
import useAppContext from 'flow/hooks/useAppContext';
import useExtensionDisplayName from 'flow/hooks/useExtensionDisplayName';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import {
    selectAssigneeChangedEventNewValue,
    selectAssigneeChangedEventPrevValue,
} from 'flow/selectors/TicketEventSelectors';
import { TicketKey } from 'flow/types/EntityKeys';
import { TicketActionEventKey } from 'flow/types/TicketEventData';
import ActionEventRow from './ActionEventRow';
import useActorTooltipLine from './useActorTooltipLine';

interface Props {
    ticketKey: TicketKey;
    eventKey: TicketActionEventKey;
}

const ActionEventAssigneeChanged = ({ ticketKey, eventKey }: Props) => {
    const { i18next } = useAppContext();

    const newValue = useAppSelector((state) =>
        selectAssigneeChangedEventNewValue(state, ticketKey, eventKey),
    );
    const prevValue = useAppSelector((state) =>
        selectAssigneeChangedEventPrevValue(state, ticketKey, eventKey),
    );
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);

    const newAssigneeLabel = useExtensionDisplayName(newValue);
    const prevAssigneeLabel = useExtensionDisplayName(prevValue);
    const actorLine = useActorTooltipLine(ticketKey, eventKey);

    const assigneeIsSet = !!newValue;
    const newAssigneeIsSelf = !!newValue && newValue === ownExtensionKey;

    const tooltipParts: string[] = [
        i18next.t('tickets.tooltip.previously', {
            value:
                prevAssigneeLabel ?? i18next.t('tickets.unassigned.singular'),
        }),
    ];
    if (actorLine) {
        tooltipParts.push(actorLine);
    }

    return (
        <ActionEventRow
            ticketKey={ticketKey}
            eventKey={eventKey}
            tooltipLabel={tooltipParts.join(' · ')}
        >
            {assigneeIsSet ? (
                <Trans
                    i18n={i18next}
                    i18nKey={
                        newAssigneeIsSelf
                            ? 'tickets.assignee.changed.self'
                            : 'tickets.assignee.changed'
                    }
                    values={
                        newAssigneeIsSelf
                            ? undefined
                            : { assignee: newAssigneeLabel }
                    }
                    components={[<strong key="assignee" />]}
                />
            ) : (
                <Trans
                    i18n={i18next}
                    i18nKey="tickets.assignee.unassigned"
                    components={[<strong key="unassigned" />]}
                />
            )}
        </ActionEventRow>
    );
};

export default React.memo(ActionEventAssigneeChanged);
